/* eslint-disable no-await-in-loop */
import { useCallback, useContext } from 'react';
import type { ChannelData } from 'stream-chat';
import {
  ContextualChatContext,
  LeftSidePanelPageType,
  ChannelType,
} from 'contexts';
import { ContextualChatController, ListingController } from 'api';
import type { RootState } from 'components/store/store';
import { store } from 'components/store/store';
import { subSeconds, isAfter } from 'date-fns';

interface SelectedUser {
  // This id corresponds to the user id, not the streamChatId
  id: string;
  name?: string;
  avatarFilestackId?: string;
  bbUserId?: string;
}

interface BaseStartChatParams {
  userId: string;
  selectedUsers: SelectedUser[];
  channelName?: string;
}

type MessagingParams = BaseStartChatParams & {
  channelType?: ChannelType.messaging;
};

type ListingParams = BaseStartChatParams & {
  channelType?: ChannelType.listing;
  listingId: string;
  listingThumbnail?: string;
  userIdToCreateChannelId?: string;
};

type ListingDataType = {
  listingName: string;
  listingThumbnail: string;
};

type StartChatParams = MessagingParams | ListingParams;

const getCommonItems = (...arrays: string[][]): string[] => {
  if (arrays.length === 0) return [];

  const commonSet = arrays.reduce<Set<string>>((acc, arr) => {
    const currentSet = new Set(arr);
    return new Set([...acc].filter(item => currentSet.has(item)));
  }, new Set(arrays[0]));

  return [...commonSet];
};

export const useStartChat = () => {
  const {
    client,
    setChannel,
    setLeftSidePanelPage,
    setCreatingChatLoading,
    setChannelIdToTop,
    setViewDetailsPanel,
  } = useContext(ContextualChatContext);

  const startChat = useCallback(
    async (params: StartChatParams) => {
      const {
        userId, // This is already in Chat Stream ID Format
        selectedUsers,
        channelType = ChannelType.messaging,
      } = params;

      if (!selectedUsers.length) return;
      // This displays the loading state in the ContextualChat modal
      setCreatingChatLoading(true);

      const indexKey = channelType === 'listing' ? 'id' : 'bbUserId';

      let selectedUserIds: string[];
      try {
        selectedUserIds = await ContextualChatController.getStreamChatUserIds(
          selectedUsers.map(user => user[indexKey]),
        );
        if (!selectedUserIds?.length) {
          throw new Error('No valid users to start a chat');
        }
      } catch (error) {
        console.error('There was an error getStreamChatUserIds', error.message);
        setCreatingChatLoading(false);
        return;
      }

      const allUserIds = [userId, ...selectedUserIds].filter(id => id);

      const { users } = await client.queryUsers({
        id: { $in: allUserIds },
      });

      let commonMlsTypes = getCommonItems(
        ...users.map(user => user.mlsTypes ?? []),
      );

      if (!commonMlsTypes.length) {
        commonMlsTypes = getCommonItems(...users.map(user => user.teams ?? []));
      }

      if (!commonMlsTypes.length) {
        throw new Error(
          "Selected Users doesn't have required premissions to start a chat",
        );
      }
      let listingData: ListingDataType;
      if (channelType === ChannelType.listing) {
        if (!('listingId' in params)) {
          throw new Error('listingId is required for listing channels');
        }
        try {
          let data;
          const { listing } = (store.getState() as RootState).chatButton;
          if (listing && listing._id === params.listingId) {
            data = listing;
          } else {
            data = await ListingController.getListing(params.listingId);
          }
          listingData = {
            listingName: `${data.line1}${data.line2 ? ` ${data.line2}` : ''}`,
            listingThumbnail: data.thumbnail ?? '',
          };
        } catch {
          throw new Error('Error fetching listing data for a listing chat');
        }
      }

      const generateChannelName = () => {
        if (params.channelName) {
          return params.channelName;
        }

        if (channelType === ChannelType.listing) {
          const { listingId } = params as ListingParams;
          if (listingId) {
            return listingData.listingName.trim();
          }
        }
      };

      // eslint-disable-next-line no-restricted-syntax
      for (const team of commonMlsTypes) {
        try {
          const extraData: Record<string, any> = {
            members: Array.from(new Set(allUserIds)),
            team,
          };

          if (channelType === ChannelType.listing) {
            if (!('listingId' in params)) {
              throw new Error('listingId is required for listing channels');
            }
            extraData.listingId = params.listingId;
            extraData.listingThumbnail = listingData.listingThumbnail;
            extraData.name = generateChannelName();
          }

          let channelId: string | undefined;
          if (channelType === ChannelType.listing && 'listingId' in params) {
            channelId = await ContextualChatController.getListingChannelId(
              users
                .filter(
                  user => user.bbUserId === params.userIdToCreateChannelId,
                )
                .map(u => u.id)[0] ?? userId,
              params.listingId,
            );
          }

          const channel = client.channel(
            channelType,
            channelId,
            extraData as ChannelData,
          );

          await channel.watch();

          const [refetchedChannel] = await client.queryChannels(
            { cid: channel.cid },
            {},
            { limit: 1, watch: true },
          );

          const activeChannel = refetchedChannel || channel;

          setChannel(activeChannel);

          // This delays is needed that we don't attempt to reorganize the channel list prior to a new chat being rendered and 'not found' in the list
          const cutoffTime = subSeconds(new Date(), 30); // 30 seconds
          // Dispatch the update to move exisiting channel to the top of the channel list if it is an existing channel. New channels are already on top by default
          if (isAfter(cutoffTime, new Date(activeChannel.data.created_at))) {
            setChannelIdToTop(activeChannel.cid);
          }

          // Ensure the details panel on the right is closed when starting a new chat
          setViewDetailsPanel(false);

          break;
        } catch (error: any) {
          // if reached last common mls and still not able to start chat then report the error
          if (team === commonMlsTypes[commonMlsTypes.length - 1]) {
            // eslint-disable-next-line no-console
            console.error('Error creating new chat:', error);
          }
        }
      }

      setCreatingChatLoading(false);
      setLeftSidePanelPage(LeftSidePanelPageType.ChannelList);
    },
    [client, setChannel, setCreatingChatLoading, setLeftSidePanelPage],
  );

  return { startChat };
};

export default useStartChat;
